import { REPORT_COLORS } from '../../constants';
import type { EnergySummary } from '../../../types/report';
import './EnergyFlow.css';

const WIDTH = 720;
const HEIGHT = 300;
const NODE_W = 13;
const PAD_TOP = 34;
const PAD_BOTTOM = 26;
const GAP = 26;

interface Flow {
  label: string;
  mw: number;
  color: string;
  from: 'import' | 'renewable' | 'fossil';
  to: 'island' | 'offisland';
}

/**
 * A Sankey-style diagram of average power flow. Bars on the left are where the
 * electricity comes from, bars on the right are where it goes, and ribbon width
 * is proportional to megawatts — so the paradox of importing while exporting is
 * visible as geometry rather than asserted in prose.
 */
export function EnergyFlow({ energy }: { energy: EnergySummary }) {
  const renewableToIsland = energy.avgRenewableUsedMw;
  const renewableToExport = energy.avgRenewableExportedMw;
  // Fossil is derived so the inflows reconcile exactly with island demand.
  const fossilToIsland = Math.max(
    0,
    energy.avgLoadMw - energy.avgImportMw - renewableToIsland,
  );

  const flows: Flow[] = ([
    { label: 'Imported from New Brunswick', mw: energy.avgImportMw, color: REPORT_COLORS.imported, from: 'import', to: 'island' },
    { label: 'On-island renewables used here', mw: renewableToIsland, color: REPORT_COLORS.renewable, from: 'renewable', to: 'island' },
    { label: 'On-island renewables exported', mw: renewableToExport, color: REPORT_COLORS.exported, from: 'renewable', to: 'offisland' },
    { label: 'On-island fossil fuel', mw: fossilToIsland, color: REPORT_COLORS.fossil, from: 'fossil', to: 'island' },
  ] as Flow[]).filter((f) => f.mw > 0);

  const totalIn = flows.reduce((s, f) => s + f.mw, 0);
  const plotH = HEIGHT - PAD_TOP - PAD_BOTTOM;

  // Left column stacks sources; right column stacks destinations. Both share one
  // MW-per-pixel scale so ribbon widths are directly comparable.
  const sources = ['import', 'renewable', 'fossil'] as const;
  const destinations = ['island', 'offisland'] as const;

  const sourceTotals = sources.map((s) => flows.filter((f) => f.from === s).reduce((a, f) => a + f.mw, 0));
  const destTotals = destinations.map((d) => flows.filter((f) => f.to === d).reduce((a, f) => a + f.mw, 0));

  const activeSources = sources.filter((_, i) => sourceTotals[i] > 0);
  const activeDests = destinations.filter((_, i) => destTotals[i] > 0);
  const scale = (plotH - GAP * Math.max(activeSources.length - 1, activeDests.length - 1)) / totalIn;

  const layout = (keys: readonly string[], totals: number[]) => {
    const map = new Map<string, { y: number; h: number; cursor: number }>();
    let y = PAD_TOP;
    keys.forEach((k, i) => {
      if (totals[i] <= 0) return;
      const h = totals[i] * scale;
      map.set(k, { y, h, cursor: y });
      y += h + GAP;
    });
    return map;
  };

  const left = layout(sources, sourceTotals);
  const right = layout(destinations, destTotals);

  const ribbon = (f: Flow) => {
    const l = left.get(f.from)!;
    const r = right.get(f.to)!;
    const h = f.mw * scale;
    const y0 = l.cursor;
    const y1 = r.cursor;
    l.cursor += h;
    r.cursor += h;

    const x0 = 150 + NODE_W;
    const x1 = WIDTH - 150 - NODE_W;
    const mid = (x0 + x1) / 2;

    return {
      d: `M ${x0},${y0} C ${mid},${y0} ${mid},${y1} ${x1},${y1}
          L ${x1},${y1 + h} C ${mid},${y1 + h} ${mid},${y0 + h} ${x0},${y0 + h} Z`,
      f,
    };
  };

  const ribbons = flows.map(ribbon);

  const NODE_LABEL: Record<string, string> = {
    import: 'New Brunswick',
    renewable: 'Wind & solar',
    fossil: 'Diesel',
    island: 'Used on PEI',
    offisland: 'Sent off-island',
  };

  const nodeTotal = (k: string, isLeft: boolean) =>
    flows.filter((f) => (isLeft ? f.from : f.to) === k).reduce((a, f) => a + f.mw, 0);

  return (
    <figure className="EnergyFlow">
      <figcaption className="EnergyFlow-caption">
        Average power flow across the whole record, in megawatts. Ribbon width is
        proportional to the amount of electricity.
      </figcaption>

      <div className="EnergyFlow-plot">
        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img"
             aria-label="Flow diagram of average electricity supply and where it is used">
          <text x={150 + NODE_W} y={16} className="EnergyFlow-colHead" textAnchor="end">WHERE IT COMES FROM</text>
          <text x={WIDTH - 150 - NODE_W} y={16} className="EnergyFlow-colHead">WHERE IT GOES</text>

          {ribbons.map(({ d, f }) => (
            <path key={f.label} d={d} fill={f.color} opacity={0.42}>
              <title>{`${f.label}: ${f.mw.toFixed(1)} MW`}</title>
            </path>
          ))}

          {[...left.entries()].map(([k, n]) => (
            <g key={`l-${k}`}>
              <rect x={150} y={n.y} width={NODE_W} height={n.h} rx={2}
                    fill={k === 'import' ? REPORT_COLORS.imported
                        : k === 'renewable' ? REPORT_COLORS.renewable : REPORT_COLORS.fossil} />
              <text x={142} y={n.y + n.h / 2 - 3} className="EnergyFlow-node" textAnchor="end">
                {NODE_LABEL[k]}
              </text>
              <text x={142} y={n.y + n.h / 2 + 12} className="EnergyFlow-mw" textAnchor="end">
                {nodeTotal(k, true).toFixed(1)} MW
              </text>
            </g>
          ))}

          {[...right.entries()].map(([k, n]) => (
            <g key={`r-${k}`}>
              <rect x={WIDTH - 150 - NODE_W} y={n.y} width={NODE_W} height={n.h} rx={2}
                    fill={k === 'island' ? REPORT_COLORS.imported : REPORT_COLORS.exported} />
              <text x={WIDTH - 142} y={n.y + n.h / 2 - 3} className="EnergyFlow-node">
                {NODE_LABEL[k]}
              </text>
              <text x={WIDTH - 142} y={n.y + n.h / 2 + 12} className="EnergyFlow-mw">
                {nodeTotal(k, false).toFixed(1)} MW
              </text>
            </g>
          ))}
        </svg>
      </div>

      <table className="EnergyFlow-table">
        <caption>Average power flow, megawatts</caption>
        <thead>
          <tr><th scope="col">Flow</th><th scope="col">MW</th></tr>
        </thead>
        <tbody>
          {flows.map((f) => (
            <tr key={f.label}>
              <th scope="row">
                <span className="EnergyFlow-swatch" style={{ background: f.color }} aria-hidden="true" />
                {f.label}
              </th>
              <td>{f.mw.toFixed(1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
